import { appRoutes } from "@/app-routes";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/dropdown-menu";
import RemergeLogo from "@/components/remerge-logo";
import RemergeLogoText from "@/components/remerge-logo-text";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from "@/components/sidebar";
import { useTheme } from "@/lib/theme-provider";
import {
  ChartNoAxesCombined,
  ChevronDown,
  ChevronsUpDown,
  FilterIcon,
  FlaskConical,
  Landmark,
  LayoutDashboard,
  Monitor,
  Moon,
  Scale,
  Settings,
  ShieldCheck,
  Sun,
  Target,
  TimerReset,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";

type NavSubItem = {
  to: string;
  title?: string;
};

type NavItem = {
  to?: string;
  title?: string;
  icon: LucideIcon;
  subItems?: NavSubItem[];
};

const teamLinks: NavItem[] = [
  { to: "/team-goals", icon: Target },
  { to: "/evidence-gates", icon: ShieldCheck },
  { to: "/jira-metrics", icon: TimerReset },
  { to: "/business", icon: ChartNoAxesCombined },
];

const campaignLinks: NavItem[] = [
  { to: "/", icon: LayoutDashboard },
  { to: "/performance", icon: Landmark },
  { to: "/analysis", icon: ChartNoAxesCombined },
  { to: "/campaign-filter", icon: FilterIcon },
];

const productLinks: NavItem[] = [
  { to: "/efficiency", icon: Wallet },
  {
    to: "/experiments",
    icon: FlaskConical,
    subItems: [
      { to: "/experiments", title: "Overview" },
      { to: "/experiments/runtime" },
      { to: "/experiments/campaign-assignment" },
    ],
  },
  {
    title: "Calibration",
    icon: Scale,
    subItems: [{ to: "/calibration-analysis" }, { to: "/calibration-plots" }],
  },
];

function normalizeRoutePath(path: string): string {
  return path === "/" ? path : path.replace(/^\//, "");
}

function getRouteTitle(path: string): string | undefined {
  const route = appRoutes.find(appRoute => appRoute.path === normalizeRoutePath(path));
  return route?.handle.title;
}

function getNavTitle(item: { to?: string; title?: string }): string {
  return item.title ?? (item.to ? (getRouteTitle(item.to) ?? item.to) : "");
}

function isSubItemActive(pathname: string, subPath: string): boolean {
  if (subPath === "/experiments") {
    return pathname === "/experiments" || pathname === "/experiments/create" || /^\/experiments\/\d+$/.test(pathname);
  }

  return pathname === subPath;
}

export function AppSidebar() {
  const { state } = useSidebar();
  const { theme, setTheme } = useTheme();
  let location = useLocation();
  return (
    <Sidebar variant="sidebar" collapsible="icon" className="z-50">
      <SidebarContent>
        <NavLink to="/">
          <div
            className={`gap-1 px-2 ${state === "expanded" ? "justify-start" : "px-3 justify-center"}  mt-6 pb-6 flex width-full`}
          >
            <RemergeLogo />
            {state === "expanded" && <RemergeLogoText />}
          </div>
        </NavLink>
        {(
          [
            { label: "Team", links: teamLinks },
            { label: "Clients", links: campaignLinks },
            { label: "Product", links: productLinks },
          ] as const
        ).map(group => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.links.map(link => (
                  <SidebarMenuItem key={link.to ?? link.title}>
                    {link.subItems ? (
                      <Collapsible defaultOpen className="group/collapsible">
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton>
                            <link.icon />
                            <span>{getNavTitle(link)}</span>
                            <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {link.subItems.map(subItem => (
                              <SidebarMenuSubItem key={subItem.to}>
                                <SidebarMenuSubButton asChild isActive={isSubItemActive(location.pathname, subItem.to)}>
                                  <NavLink to={subItem.to}>
                                    <span>{getNavTitle(subItem)}</span>
                                  </NavLink>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </Collapsible>
                    ) : (
                      <SidebarMenuButton
                        asChild
                        variant="default"
                        isActive={link.to ? location.pathname === link.to : false}
                      >
                        <NavLink to={link.to!}>
                          <link.icon />
                          <span>{getNavTitle(link)}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    )}
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton>
                  <Settings />
                  <span>Settings</span>
                  <ChevronsUpDown className="ml-auto" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="right"
                align="end"
                className="w-[--radix-popper-anchor-width] bg-sidebar border-sidebar-border text-sidebar-foreground"
              >
                <DropdownMenuLabel>Theme</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-sidebar-border" />
                <DropdownMenuRadioGroup
                  value={theme}
                  onValueChange={value => setTheme(value as "light" | "dark" | "system")}
                >
                  <DropdownMenuRadioItem
                    value="light"
                    className="focus:bg-sidebar-accent focus:text-sidebar-accent-foreground"
                  >
                    <Sun className="mr-2 h-4 w-4" />
                    Light
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem
                    value="system"
                    className="focus:bg-sidebar-accent focus:text-sidebar-accent-foreground"
                  >
                    <Monitor className="mr-2 h-4 w-4" />
                    System
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem
                    value="dark"
                    className="focus:bg-sidebar-accent focus:text-sidebar-accent-foreground"
                  >
                    <Moon className="mr-2 h-4 w-4" />
                    Dark
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
